import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Header } from '../../components/layout/Header';
import { useAuth } from '../../lib/auth/AuthContext';
import {
  fetchAccountMembers,
  inviteAccountMember,
  removeAccountMember,
} from '../../lib/api/accounts';
import {
  Button,
  ConfirmDialog,
  EmptyState,
  PageHeader,
  StatusBadge,
  type StatusTone,
} from '../../components/ui';
import { EMAIL_PATTERN, type AccountRole } from '../../../../shared/types/accounts';

/**
 * /account/members — member-management page for the current operator's
 * primary account. Lists active + invited members; owners can invite
 * new members and revoke existing ones.
 */
export const AccountMembersPage: React.FC = () => {
  const { currentAccount } = useAuth();
  const queryClient = useQueryClient();
  const accountId = currentAccount?.id ?? '';
  const isOwner = currentAccount?.role === 'owner';

  const query = useQuery({
    queryKey: ['account', accountId, 'members'],
    queryFn: () => fetchAccountMembers(accountId),
    enabled: accountId.length > 0,
  });

  return (
    <div data-testid="account-members-page" className="min-h-screen bg-slate-50">
      <Header title="Members" />
      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-8 space-y-5">
        <PageHeader
          eyebrow={currentAccount && <span className="font-mono">{currentAccount.id}</span>}
          title={
            <>
              {currentAccount?.name ?? 'Account'}{' '}
              <span className="text-slate-400 font-normal">· members</span>
            </>
          }
          description="Everyone with access to this account's projects, repos, and health data."
        />

        {isOwner && accountId && (
          <InviteForm
            accountId={accountId}
            onInvited={() =>
              queryClient.invalidateQueries({ queryKey: ['account', accountId, 'members'] })
            }
          />
        )}

        {query.isLoading && <div className="text-sm text-slate-500">Loading…</div>}
        {query.isError && (
          <div role="alert" className="text-sm text-danger-700 bg-danger-50 border border-danger-100 rounded-md px-3 py-2">
            {(query.error as Error).message}
          </div>
        )}
        {query.isSuccess && query.data.length === 0 && (
          <EmptyState icon="👥" title="No members yet" />
        )}
        {query.isSuccess && query.data.length > 0 && (
          <MembersTable
            members={query.data}
            isOwner={isOwner}
            accountId={accountId}
            onRemoved={() =>
              queryClient.invalidateQueries({ queryKey: ['account', accountId, 'members'] })
            }
          />
        )}
      </main>
    </div>
  );
};

const InviteForm: React.FC<{
  accountId: string;
  onInvited: () => void;
}> = ({ accountId, onInvited }) => {
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<AccountRole>('member');
  const [submitted, setSubmitted] = useState(false);

  const mutation = useMutation({
    mutationFn: () => inviteAccountMember(accountId, { email: email.trim().toLowerCase(), role }),
    onSuccess: () => {
      setEmail('');
      setSubmitted(false);
      onInvited();
    },
  });

  const trimmed = email.trim();
  const invalid = submitted && !EMAIL_PATTERN.test(trimmed);

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        setSubmitted(true);
        if (!EMAIL_PATTERN.test(trimmed)) return;
        mutation.mutate();
      }}
      className="bg-white border border-slate-200 rounded-xl shadow-card p-4 space-y-3"
      aria-label="Invite a member"
    >
      <h2 className="text-sm font-semibold text-slate-800">Invite by email</h2>
      <div className="flex gap-2">
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="teammate@example.com"
          aria-invalid={invalid || undefined}
          disabled={mutation.isPending}
          className="flex-1 rounded-md border border-slate-300 px-3 py-2 text-sm"
        />
        <select
          value={role}
          onChange={(e) => setRole(e.target.value as AccountRole)}
          disabled={mutation.isPending}
          className="rounded-md border border-slate-300 px-2 py-2 text-sm bg-white"
        >
          <option value="member">Member</option>
          <option value="owner">Owner</option>
        </select>
        <Button
          type="submit"
          variant="primary"
          size="md"
          loading={mutation.isPending}
        >
          {mutation.isPending ? 'Inviting…' : 'Invite'}
        </Button>
      </div>
      {invalid && <p className="text-xs text-danger-700">Enter a valid email</p>}
      {mutation.isError && (
        <p role="alert" className="text-xs text-danger-700">
          {(mutation.error as Error).message}
        </p>
      )}
      <p className="text-xs text-slate-500">
        The invitee will see this account on their next sign-in. They sign up
        themselves via the regular form — we just match their email when they log in.
      </p>
    </form>
  );
};

const MembersTable: React.FC<{
  members: Awaited<ReturnType<typeof fetchAccountMembers>>;
  isOwner: boolean;
  accountId: string;
  onRemoved: () => void;
}> = ({ members, isOwner, accountId, onRemoved }) => (
  <div className="bg-white border border-slate-200 rounded-xl shadow-card overflow-hidden">
    <table className="w-full text-sm" data-testid="members-table">
      <thead className="bg-slate-50 border-b border-slate-200">
        <tr className="text-left">
          <th className="px-4 py-2 text-2xs uppercase tracking-wide text-slate-500 font-semibold">Email</th>
          <th className="px-4 py-2 text-2xs uppercase tracking-wide text-slate-500 font-semibold">Role</th>
          <th className="px-4 py-2 text-2xs uppercase tracking-wide text-slate-500 font-semibold">Status</th>
          <th className="px-4 py-2 text-2xs uppercase tracking-wide text-slate-500 font-semibold">Joined</th>
          {isOwner && <th />}
        </tr>
      </thead>
      <tbody>
        {members.map((m) => (
          <MemberRow
            key={m.email}
            member={m}
            isOwner={isOwner}
            accountId={accountId}
            onRemoved={onRemoved}
          />
        ))}
      </tbody>
    </table>
  </div>
);

const MemberRow: React.FC<{
  member: Awaited<ReturnType<typeof fetchAccountMembers>>[number];
  isOwner: boolean;
  accountId: string;
  onRemoved: () => void;
}> = ({ member, isOwner, accountId, onRemoved }) => {
  const [confirming, setConfirming] = useState(false);
  const mutation = useMutation({
    mutationFn: () => removeAccountMember(accountId, member.email),
    onSuccess: () => {
      setConfirming(false);
      onRemoved();
    },
  });
  const statusTone: StatusTone =
    member.status === 'active' ? 'success' : 'warning';
  return (
    <tr className="border-b border-slate-100 last:border-b-0 hover:bg-slate-50/60">
      <td className="px-4 py-3 font-mono text-xs">{member.email}</td>
      <td className="px-4 py-3">
        <StatusBadge tone={member.role === 'owner' ? 'info' : 'neutral'}>
          {member.role}
        </StatusBadge>
      </td>
      <td className="px-4 py-3">
        <StatusBadge tone={statusTone} dot>
          {member.status}
        </StatusBadge>
      </td>
      <td className="px-4 py-3 text-xs text-slate-500 whitespace-nowrap">
        {member.joinedAt
          ? new Date(member.joinedAt).toLocaleDateString()
          : member.invitedAt
          ? `invited ${new Date(member.invitedAt).toLocaleDateString()}`
          : '—'}
      </td>
      {isOwner && (
        <td className="px-4 py-3 text-right">
          <button
            type="button"
            onClick={() => setConfirming(true)}
            disabled={mutation.isPending}
            className="text-xs text-danger-600 hover:underline disabled:opacity-40"
          >
            {mutation.isPending ? 'Removing…' : 'Remove'}
          </button>
          <ConfirmDialog
            open={confirming}
            title={`Remove ${member.email}?`}
            description="They lose access to all projects in this account. You can invite them again later."
            confirmLabel="Remove member"
            tone="danger"
            pending={mutation.isPending}
            onConfirm={() => mutation.mutate()}
            onCancel={() => setConfirming(false)}
            testId="members-remove-confirm"
          >
            {mutation.isError && (
              <p role="alert" className="text-xs text-danger-700">
                {(mutation.error as Error).message}
              </p>
            )}
          </ConfirmDialog>
        </td>
      )}
    </tr>
  );
};

export default AccountMembersPage;
